// Web variant of the SDK's `WaterGlassSvg`.
//
// The SDK component animates the water fill by driving the fill <Rect>'s
// `y` / `height` through react-native-reanimated's `useAnimatedProps`.
// Reanimated's animated-props path does not apply to react-native-svg
// elements on RN-Web, so on the web the fill rect renders at height 0 —
// the glass shows empty no matter how much water is logged.
//
// This variant computes `y` / `height` statically from `fillPercent`, so
// the fill renders correctly on web. It drops the fill animation (the
// level snaps instead of easing) — an acceptable trade for a working
// visual. Wired in via the `waterGlassSvgShimPlugin` in vite.config.mts.
//
// The glass-outline / clip / gradient geometry below is copied verbatim
// from the SDK component so the rendered glass is pixel-identical; only
// the fill <Rect> differs (static vs. reanimated).

import Svg, {
  ClipPath,
  Defs,
  G,
  LinearGradient,
  Path,
  Rect,
  Stop,
} from 'react-native-svg';

// Glass container bounds for fill calculation (SDK constants).
const GLASS_TOP_Y = 40;
const GLASS_BOTTOM_Y = 137;
const GLASS_HEIGHT = GLASS_BOTTOM_Y - GLASS_TOP_Y;

interface WaterGlassSvgProps {
  fillPercent: number;
  size?: number;
}

export function WaterGlassSvg({ fillPercent, size = 120 }: WaterGlassSvgProps) {
  const clampedFill = Math.min(100, Math.max(0, fillPercent));
  const fillHeight = (clampedFill / 100) * GLASS_HEIGHT;
  const fillTopY = GLASS_BOTTOM_Y - fillHeight;

  return (
    <Svg width={size} height={size} viewBox="0 0 175 175" fill="none">
      <Defs>
        <LinearGradient
          id="glassOutlineGradient"
          x1="126.193"
          y1="137.081"
          x2="22.869"
          y2="130.845"
          gradientUnits="userSpaceOnUse"
        >
          <Stop stopColor="#21668A" />
          <Stop offset="1" stopColor="#32A7CA" />
        </LinearGradient>
        <LinearGradient
          id="highlightGradient"
          x1="112.487"
          y1="88.1649"
          x2="104.522"
          y2="88.0194"
          gradientUnits="userSpaceOnUse"
        >
          <Stop stopColor="#21668A" />
          <Stop offset="1" stopColor="#32A7CA" />
        </LinearGradient>
        <LinearGradient
          id="waterFillGradient"
          x1="87.5"
          y1={GLASS_BOTTOM_Y}
          x2="87.5"
          y2={GLASS_TOP_Y}
          gradientUnits="userSpaceOnUse"
        >
          <Stop stopColor="#4AA8D8" />
          <Stop offset="1" stopColor="#87CEEB" />
        </LinearGradient>
        <ClipPath id="waterClip">
          <Path d="M48.86 46.5 L65.625 135.467 C72.94 136.59 80.22 137.13 87.5 137.08 C94.77 137.12 102.04 136.57 109.22 135.43 L126 47.25 C124.35 46.27 110.25 39.67 87.5 39.67 C64.75 39.67 50.75 43.54 48.86 46.5 Z" />
        </ClipPath>
      </Defs>
      {/* Water fill — static rect, clipped to the glass interior. */}
      <G clipPath="url(#waterClip)">
        <Rect
          x="45"
          width="85"
          y={fillTopY}
          height={fillHeight}
          fill="url(#waterFillGradient)"
          opacity={0.85}
        />
      </G>
      {/* Glass outline. */}
      <Path
        d="M87.5 37.9223C68.25 37.9223 48.86 40.8273 48.86 46.3748C48.86 46.3748 48.86 46.3748 48.86 46.4973C48.86 46.6198 48.86 46.4973 48.86 46.4973C48.8456 46.6486 48.8456 46.8009 48.86 46.9523L64.61 134.75C64.6468 134.951 64.7526 135.132 64.9092 135.263C65.0658 135.394 65.2633 135.467 65.4675 135.467H65.625H65.7475C72.9428 136.592 80.2176 137.13 87.5 137.077C94.7718 137.117 102.035 136.567 109.218 135.432H109.323H109.48C109.684 135.432 109.882 135.359 110.038 135.228C110.195 135.097 110.301 134.916 110.338 134.715L126 47.2498C126.125 46.9563 126.19 46.6411 126.193 46.3223C126.14 40.8273 106.75 37.9223 87.5 37.9223ZM108.745 133.752C101.717 134.844 94.6124 135.37 87.5 135.327C80.3697 135.368 73.2476 134.835 66.2025 133.735L51.17 49.4723C56.875 52.9723 72.17 54.8448 87.5 54.8448C102.83 54.8448 118.038 53.0948 123.778 49.5948L108.745 133.752ZM124.25 46.8298C122.85 49.5773 109.13 53.0773 87.5 53.0773C64.925 53.0773 50.995 49.2623 50.645 46.4798V46.3398C50.75 43.5398 64.75 39.6723 87.5 39.6723C110.25 39.6723 123.97 43.4698 124.355 46.2698L124.25 46.8298Z"
        fill="url(#glassOutlineGradient)"
      />
      {/* Rim highlight. */}
      <Path
        d="M107.275 88.1649H107.432C107.637 88.1641 107.834 88.092 107.991 87.961C108.147 87.8299 108.253 87.6482 108.29 87.4474L112.472 64.0324C112.514 63.8003 112.462 63.5612 112.328 63.3675C112.193 63.1739 111.987 63.0417 111.755 62.9999C111.523 62.9581 111.284 63.0102 111.09 63.1448C110.897 63.2794 110.764 63.4853 110.722 63.7174L106.558 87.1499C106.537 87.2637 106.54 87.3804 106.565 87.4931C106.591 87.6059 106.638 87.7126 106.705 87.8069C106.771 87.9013 106.856 87.9815 106.954 88.043C107.052 88.1044 107.161 88.1458 107.275 88.1649Z"
        fill="url(#highlightGradient)"
      />
    </Svg>
  );
}
